import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CardMedia,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  AccountBalance as AccountBalanceIcon,
  CreditCard as CreditCardIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Warning as WarningIcon,
  AttachFile as AttachFileIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Payment as PaymentIcon,
  SwapHoriz as SwapHorizIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import COLORS from '../../../Utils/colors';
import { request } from '../../../Utils/request';
import { getSignedUploadUrls, uploadFileToSignedUrl, getPublicUrlFromFileKey } from '../../../services/API/Api';
import { paiseToRupees, rupeesToPaise } from '../../../Utils/currencyConverter';
import { useSelector } from 'react-redux';

interface Transaction {
  _id: string;
  reference: {
    tripId: string;
    paymentId: string;
  };
  type: 'credit' | 'debit' | 'refund' | 'payout';
  source: string;
  status: string;
  grossAmount: number;
  netAmount: number;
  note: string;
  createdAt: string;
}

interface BankDetails {
  accountType: string;
  bankName: string;
  ifscCode: string;
  phoneNumber: string;
  cancelledCheque?: File;
  cancelledChequeImage?: string; // URL for displaying existing image
}

interface WalletState {
  balance: number;
  hasBankDetails: boolean;
  transactions: Transaction[];
  isLoading: boolean;
}


const TransactionsTab: React.FC = () => {
  const isLoggedIn = useSelector((state: any) => state.auth.isLoggedIn);
  const [bankDetails, setBankDetails] = useState<BankDetails>({
    accountType: '',
    bankName: '',
    ifscCode: '',
    phoneNumber: '',
    cancelledChequeImage: '',
  });
  const [walletState, setWalletState] = useState<WalletState>({
    balance: 0,
    hasBankDetails: false,

    transactions: [],
    isLoading: false,
  });

  const [withdrawalAmount, setWithdrawalAmount] = useState<number>(1000);
  const [showWithdrawalDialog, setShowWithdrawalDialog] = useState(false);
  const [showBankDetailsDialog, setShowBankDetailsDialog] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [showChequeImageDialog, setShowChequeImageDialog] = useState(false);

  const handleWithdrawFunds = () => {
    if (walletState.balance >= 1000) {
      setShowWithdrawalDialog(true);
    }
  };

  useEffect(() => {
    const fetchWalletData = async () => {
      try {
        setWalletState(prev => ({ ...prev, isLoading: true }));
        const response = await request('/profile/me/wallet', {
          method: 'GET',
          authorization: true,
        }) as any;

        // Update wallet state with API response
        if (response?.status) {
          setWalletState(prev => ({
            ...prev,
            balance: response.data.balance || prev.balance,
            hasBankDetails: response.data.hasBankDetails || false,
            transactions: response.data.transactions || [],
            isLoading: false,
          }));
        } else {
          // If no data received, just stop loading
          setWalletState(prev => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        console.error('Error fetching wallet data:', error);
        setWalletState(prev => ({ ...prev, isLoading: false }));
      }
    };

    const fetchBankDetails = async () => {
      try {
        const response = await request('/profile/bank-information', {
          method: 'GET',
          authorization: true,
        }) as any;
        
        if(response?.status === true ) {
          console.log('response.data.bankDetail',response.data.bankInformation);
          const bankData = response.data.bankInformation;
          
          let cancelledChequeImageUrl = undefined;
          if (bankData.canceledCheque?.fileKey) {
            try {
              // Use "profile" folder since cancelled cheque is uploaded to profile folder
              const publicUrlResponse = await getPublicUrlFromFileKey(bankData.canceledCheque.fileKey, 'profile');
              
              if (publicUrlResponse?.data?.files?.[0]?.viewUrl) {
                cancelledChequeImageUrl = publicUrlResponse.data.files[0].viewUrl;
              } else {
                console.error('No viewUrl found in response:', publicUrlResponse);
              }
            } catch (error) {
              console.error('Error getting public URL for cancelled cheque:', error);
            }
          } else {
            console.log('No canceledCheque.fileKey found in bankData');
          }
          
          setBankDetails({
            accountType: bankData.accountType || '',
            bankName: bankData.bankName || '',
            ifscCode: bankData.ifscCode || '',
            phoneNumber: bankData.phoneNumber || '',
            cancelledChequeImage: cancelledChequeImageUrl
          });
        } else {
          console.log('API response status is not true:', response);
        }
      } catch (error) {
        console.error('Error fetching bank details:', error);
      }
    };

    if(isLoggedIn) {
      fetchWalletData();
      fetchBankDetails();
    }

  
  }, [])

  const confirmWithdrawal = async () => {
    try {
      // Validate withdrawal amount
      if (!withdrawalAmount || withdrawalAmount <= 0) {
        alert('Error: Please enter a valid amount');
        return;
      }

      if (withdrawalAmount > walletState.balance) {
        alert('Error: Insufficient balance');
        return;
      }
      
      setWalletState(prev => ({ ...prev, isLoading: true }));
      
      const response = await request('/profile/withdraw', {
        method: 'POST',
        authorization: true,
        body: {
          amount: rupeesToPaise(withdrawalAmount),
        },
      }) as any;

      if (response?.status) {
        // Update balance and transactions from API response
        setWalletState(prev => ({
          ...prev,
          balance: response.data.balance || prev.balance,
          transactions: response.data.transactions || prev.transactions,
          isLoading: false,
        }));
        
        setShowWithdrawalDialog(false);
        setWithdrawalAmount(1000); // Reset to minimum amount
        
        // Show success message
        alert(`Withdrawal Request Submitted\n\nYour withdrawal request of ₹${withdrawalAmount.toFixed(2)} has been submitted successfully. The amount will be transferred to your registered bank account within 3-5 business days.`);
        console.log('Withdrawal request submitted successfully');
      } else {
        setWalletState(prev => ({ ...prev, isLoading: false }));
        alert(`Error: ${response?.message || 'Failed to submit withdrawal request'}`);
        console.error('Withdrawal failed:', response?.message);
      }
    } catch (error: any) {
      console.error('Error processing withdrawal:', error);
      setWalletState(prev => ({ ...prev, isLoading: false }));
      alert(`Error: ${error?.message || 'Failed to submit withdrawal request. Please try again.'}`);
    }
  };

  const handleBankDetailsSubmit = async (bankDetails: BankDetails) => {
    try {
      setWalletState(prev => ({ ...prev, isLoading: true }));
      
      // Handle file upload for cancelled cheque
      let canceledChequeData = null;
      if (bankDetails.cancelledCheque) {
        try {
          // Step 1: Prepare file upload request payload
          const filesProp = [{
            folder: 'profile',
            fileType: bankDetails.cancelledCheque.type,
          }];
          
          const signedUrlsData = await getSignedUploadUrls(filesProp);
          const signedFiles = signedUrlsData?.data?.files || [];
          
          if (signedFiles.length > 0) {
            const { uploadUrl, fileKey } = signedFiles[0];
            
            // Step 2: Upload file to signed URL
            const uploadResponse = await uploadFileToSignedUrl(bankDetails.cancelledCheque, uploadUrl);
            
            if (uploadResponse.status === 200) {
              canceledChequeData = {
                fileKey,
                fileType: bankDetails.cancelledCheque.type,
                featured: true
              };
            }
          }
        } catch (fileError) {
          console.error('Error uploading cancelled cheque:', fileError);
          throw new Error('Failed to upload cancelled cheque file');
        }
      }
      
   

      const payload = {
        accountType: bankDetails.accountType.toLowerCase(),
        bankName: bankDetails.bankName,
        ifscCode: bankDetails.ifscCode,
        canceledCheque: canceledChequeData
      };

      const response = await request('/profile/bank-information', {
        method: 'PUT',
        authorization: true,
        body: payload,
      }) as any;

      if (response?.status) {
        setWalletState(prev => ({
          ...prev,
          hasBankDetails: true,
          isLoading: false,
        }));
        setShowBankDetailsDialog(false);
        console.log('Bank details submitted successfully');
      } else {
        console.error('Failed to submit bank details:', response?.message);
        setWalletState(prev => ({ ...prev, isLoading: false }));
      }
    } catch (error) {
      console.error('Error submitting bank details:', error);
      setWalletState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const canWithdraw = walletState.balance >= 1000;

  const formatCurrency = (amount: number) => `₹${paiseToRupees(amount).toFixed(2)}`;

  return (
    <Box sx={{}}>
      {/* Wallet Balance Section */}
      <Stack direction="column" spacing={2} alignItems="center" sx={{ mb: 2 }}>

        <Box
          // elevation={0}
          sx={{
            p: 3,
            mb: 2,
            background: 'linear-gradient(135deg, #C3E552 10%, #53C758 100%)',
            color: 'white',
            borderRadius: 2,
            width: '100%',
            mx: 6,
          }}
        >

          <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 2, fontSize: '2.5rem' }}>
            {formatCurrency(walletState.balance)}
          </Typography>

          {/* {!canWithdraw && (
          <Alert 
            severity="warning" 
            icon={<WarningIcon />}
            sx={{ 
              mb: 2,
              bgcolor: '#FF9800',
              color: '#263238',
              '& .MuiAlert-icon': { color: '#263238' },
              '& .MuiAlert-message': { color: '#263238' }
            }}
          >
            Withdrawal Not Available. Minimum ₹1,000 required to withdraw funds from your wallet.
          </Alert>
        )} */}


        </Box>
        {showWithdrawalDialog &&
          <Stack spacing={3} sx={{ width: '100%' }}>
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Amount to Withdraw
              </Typography>
              <TextField
                fullWidth
                label="Enter Amount"
                type="number"
                value={withdrawalAmount}
                onChange={(e) => setWithdrawalAmount(Number(e.target.value))}
              // inputProps={{ min: 1000, max: walletState.balance }}
              // InputProps={{
              //   startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              // }}
              // sx={{ '& .MuiInputBase-root': { fontSize: '1rem' } }}
              />
              {/* <Alert severity="success" sx={{ mt: 1, bgcolor: '#E8F5E8', color: '#2E7D32' }}>
                Funds will be transferred to the account ending with ***4321
                </Alert> */}
            </Box>
            <Button
              variant="contained"
              onClick={confirmWithdrawal}
              sx={{
                bgcolor: '#FF6B35',
                '&:hover': { bgcolor: '#E55A2B' }
              }}
            >
              Confirm Withdrawal
            </Button>
          </Stack>
        }

        <Button
          variant="outlined"
          size="medium"
          disabled={!canWithdraw}
          onClick={handleWithdrawFunds}
          sx={{
            borderRadius: '100px',
            borderColor: canWithdraw ? '#FF6B35' : '#E0E0E0',
            color: canWithdraw ? '#FF6B35' : '#E0E0E0',
            borderWidth: 1,
            px: 4,
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            bgcolor: 'white',
            '&:hover': {
              borderColor: canWithdraw ? '#FF6B35' : '#E0E0E0',
              backgroundColor: 'white',
            },
          }}
        >
          Withdraw Funds
        </Button>
      </Stack>


      {/* Bank Details Section */}
      <Accordion
        defaultExpanded={!walletState.hasBankDetails}
        sx={{
          mb: 2,
          borderRadius: 2,
          border: '1px solid #f0f0f0',
          '& .MuiAccordion-root': { boxShadow: 'none' },
          '& .MuiAccordionSummary-root': { px: 2 },
          '& .MuiAccordionDetails-root': { px: 2, pb: 2 }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: '56px',
            '& .MuiAccordionSummary-content': { my: 1 }
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            {/* <AccountBalanceIcon sx={{ color: '#FF6B35', fontSize: '1.5rem' }} /> */}
            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
              Bank detail
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Box sx={{ gap: 2 }}>
              <TextField
                label="Account Type"
                value={bankDetails.accountType}
                fullWidth
                onChange={(e) => setBankDetails(prev => ({
                  ...prev,
                  accountType: e.target.value
                }))}
                sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem', mb: 2 } }}
              />
              <TextField
                label="Bank"
                value={bankDetails.bankName}
                fullWidth
                onChange={(e) => setBankDetails(prev => ({
                  ...prev,
                  bankName: e.target.value
                }))}
                sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem', mb: 2 } }}
              />
              <TextField
                label="IFSC Code"
                value={bankDetails.ifscCode}
                fullWidth
                onChange={(e) => setBankDetails(prev => ({
                  ...prev,
                  ifscCode: e.target.value
                }))}
                sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem', mb: 2 } }}
              />
              <TextField
                label="Phone number"
                value={bankDetails.phoneNumber}
                fullWidth
                onChange={(e) => setBankDetails(prev => ({
                  ...prev,
                  phoneNumber: e.target.value
                }))}
                sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem', mb: 2 } }}
              />
            </Box>
            <Box>
              <Typography variant="body2" gutterBottom sx={{ fontSize: '0.9rem', mb: 2 }}>
                Cancelled cheque
              </Typography>
              
              {/* Show document uploaded indicator - prioritize newly selected file over existing image */}
              {(bankDetails.cancelledCheque || bankDetails.cancelledChequeImage) && (
                <Box 
                  sx={{ 
                    mb: 2,
                    p: 2,
                    bgcolor: '#E8F5E9',
                    borderRadius: 1,
                    border: '1px solid #C8E6C9',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    '&:hover': {
                      bgcolor: '#C8E6C9'
                    }
                  }}
                  onClick={() => setShowChequeImageDialog(true)}
                >
                  <Typography variant="body2" sx={{ color: '#2E7D32', fontWeight: 500 }}>
                    Document uploaded
                  </Typography>
                  <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: '1.5rem' }} />
                </Box>
              )}
              <Button
                variant="outlined"
                component="label"
                startIcon={<AttachFileIcon />}
                fullWidth
                sx={{
                  borderColor: '#E0E0E0',
                  color: '#666',
                  '& .MuiButton-startIcon': { mr: 1 }
                }}
              >
                {(bankDetails.cancelledCheque || bankDetails.cancelledChequeImage) ? 'Change file' : 'Choose file'}
                <input type="file" hidden accept="image/*,.pdf" onChange={(e) => setBankDetails(prev => ({
                  ...prev,
                  cancelledCheque: e.target.files?.[0]
                }))} />
              </Button>

              {/* Remove file button */}
              {bankDetails.cancelledCheque && (
                <Button
                  variant="text"
                  color="error"
                  size="small"
                  onClick={() => setBankDetails(prev => ({
                    ...prev,
                    cancelledCheque: undefined
                  }))}
                  sx={{ mt: 1 }}
                >
                  Remove file
                </Button>
              )}
            </Box>
            <Button
              variant="contained"
              onClick={() => handleBankDetailsSubmit(bankDetails)}
              sx={{
                borderRadius:'100px',
                bgcolor: '#FF6B35',
                mt: 1,
                '&:hover': { bgcolor: '#E55A2B' }
              }}
            >
              Submit
            </Button>
          </Stack>
          {/* )} */}
        </AccordionDetails>
      </Accordion>

      {/* Transactions Section */}
      <Accordion
        defaultExpanded
        sx={{
          mb: 2,
          borderRadius: 2,
          border: '1px solid #f0f0f0',
          '& .MuiAccordion-root': { boxShadow: 'none' },
          '& .MuiAccordionSummary-root': { px: 2 },
          '& .MuiAccordionDetails-root': { px: 2, pb: 2 }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: '56px',
            '& .MuiAccordionSummary-content': { my: 1 }
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            {/* <CreditCardIcon sx={{ color: '#FF6B35', fontSize: '1.5rem' }} /> */}
            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
              Transactions
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <List sx={{ p: 0 }}>
            {[...walletState.transactions]
              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
              .map((transaction, index) => (
              <React.Fragment key={transaction._id}>
                <Paper
                  elevation={2}
                  sx={{
                    mb: 1,
                    p: 2,
                    borderRadius: "16px",
                    border: '1px solid #f0f0f0'
                  }}
                >
                  {(() => {
                    const isPositive = transaction.type === 'credit' || transaction.type === 'refund';
                    const isNegative = transaction.type === 'debit' || transaction.type === 'payout';
                    
                    // Choose appropriate icon
                    let IconComponent = SwapHorizIcon;
                    if (isPositive) IconComponent = TrendingUpIcon;
                    if (isNegative) IconComponent = TrendingDownIcon;
                    if (transaction.type === 'payout') IconComponent = PaymentIcon;
                    
                    // Choose appropriate color
                    const iconColor = isPositive ? '#4CAF50' : '#F44336';
                    
                    // Format reference ID
                    const refId = transaction?.reference?.tripId || transaction?.reference?.paymentId || transaction._id;
                    const formattedRefId = refId.length > 8 ? `REF${refId.slice(0, 6)}` : `REF${refId.slice(0, Math.min(6, refId.length))}`;
                    
                    // Format transaction ID
                    const txnId = transaction._id;
                    const formattedTxnId = txnId.length > 8 ? `#TXN${txnId.slice(0, 6)}` : `#TXN${txnId.slice(0, Math.min(6, txnId.length))}`;
                    
                    // Format amount with sign
                    const amount = transaction.type === 'payout' ? transaction.grossAmount : transaction.netAmount;
                    const amountWithSign = isPositive ? `+${formatCurrency(amount)}` : `-${formatCurrency(amount)}`;
                    const amountColor = isPositive ? '#4CAF50' : '#F44336';
                    
                    // Format status color
                    const statusColor = transaction.status === 'completed' || transaction.status === 'Completed' ? '#4CAF50' : '#FF6B35';
                    
                    return (
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        {/* Left side - Icon based on transaction type */}
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: '#E0E0E0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            flexShrink: 0
                          }}
                        >
                          <IconComponent sx={{ color: iconColor, fontSize: '1.2rem' }} />
                        </Box>

                        {/* Middle section - Transaction details */}
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Stack spacing={0.5}>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#FF6B35' }}>
                              Ref: {formattedRefId}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#666' }}>
                              Id: {formattedTxnId}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#666' }}>
                              {new Date(transaction.createdAt).toLocaleDateString()}
                            </Typography>
                          </Stack>
                        </Box>

                        {/* Right side - Amount, Source, and Status */}
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Stack spacing={0.5} alignItems="flex-end">
                            <Typography variant="h6" sx={{ 
                              color: amountColor, 
                              fontSize: '1rem', 
                              fontWeight: 600 
                            }}>
                              {amountWithSign}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#666' }}>
                              {transaction.source}
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              fontSize: '0.875rem', 
                              color: statusColor,
                              fontWeight: 500
                            }}>
                              {transaction.status 
                                ? transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1).toLowerCase()
                                : 'Pending'}
                            </Typography>
                          </Stack>
                        </Box>
                      </Stack>
                    );
                  })()}
                </Paper>
              </React.Fragment>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>


      {/* Bank Details Dialog */}
      <Dialog
        open={showBankDetailsDialog}
        onClose={() => setShowBankDetailsDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <AccountBalanceIcon sx={{ color: '#FF6B35' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Update Bank Details</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Account Type"
                value={bankDetails.accountType}
                onChange={(e) => setBankDetails(prev => ({
                  ...prev,
                  accountType: e.target.value
                }))}
                fullWidth
                size="small"
                sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem' } }}
              />
              <TextField
                label="Bank"
                value={bankDetails.bankName}
                fullWidth
                size="small"
                sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem' } }}
              />
              <TextField
                label="IFSC Code"
                value={bankDetails.ifscCode}
                fullWidth
                size="small"
                sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem' } }}
              />
              <TextField
                label="Phone no"
                value={bankDetails.phoneNumber}
                onChange={(e) => setBankDetails(prev => ({
                  ...prev,
                  phoneNumber: e.target.value
                }))}
                fullWidth
                size="small"
                sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem' } }}
              />
            </Box>
            <Box>
              <Typography variant="body2" gutterBottom sx={{ fontSize: '0.9rem' }}>
                Cancelled cheque
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<AttachFileIcon />}
                fullWidth
                sx={{
                  borderColor: '#E0E0E0',
                  color: '#666',
                  '& .MuiButton-startIcon': { mr: 1 }
                }}
              >
              {bankDetails.cancelledCheque ? 'Change file' : 'Choose file'}
                <input type="file" hidden accept="image/*,.pdf" />
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setShowBankDetailsDialog(false)}
            sx={{ color: '#666' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleBankDetailsSubmit(bankDetails)}
            sx={{
              bgcolor: '#FF6B35',
              px: 3,
              '&:hover': { bgcolor: '#E55A2B' }
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancelled Cheque Image Dialog */}
      <Dialog
        open={showChequeImageDialog}
        onClose={() => setShowChequeImageDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Cancelled Cheque</Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {bankDetails.cancelledCheque ? (
            // Prioritize newly selected file over existing image
            bankDetails.cancelledCheque.type === 'application/pdf' ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  📄 {bankDetails.cancelledCheque.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  PDF files can be downloaded after submission
                </Typography>
              </Box>
            ) : (
              <CardMedia
                component="img"
                image={URL.createObjectURL(bankDetails.cancelledCheque)}
                alt="Cancelled cheque preview"
                sx={{
                  width: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: 1,
                  backgroundColor: '#f5f5f5'
                }}
              />
            )
          ) : bankDetails.cancelledChequeImage ? (
            // Show existing image only if no new file is selected
            bankDetails.cancelledChequeImage.endsWith('.pdf') || bankDetails.cancelledChequeImage.includes('application/pdf') ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  PDF Document
                </Typography>
                <Button
                  variant="outlined"
                  href={bankDetails.cancelledChequeImage}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open PDF
                </Button>
              </Box>
            ) : (
              <CardMedia
                component="img"
                image={bankDetails.cancelledChequeImage}
                alt="Cancelled cheque"
                sx={{
                  width: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: 1,
                  backgroundColor: '#f5f5f5'
                }}
                onError={(e) => {
                  console.error('Failed to load image:', bankDetails.cancelledChequeImage);
                }}
              />
            )
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No document available
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setShowChequeImageDialog(false)}
            variant="contained"
            sx={{
              bgcolor: '#FF6B35',
              '&:hover': { bgcolor: '#E55A2B' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransactionsTab;
